/**
 * SetPopup — materiaal-set aanmaken/bewerken: naam, omschrijving, icoon+kleur,
 * materialen (types) beheren, archiveren.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Dialog, DialogContent, TextField, IconButton, Chip,
  Autocomplete, CircularProgress, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import IconColorPicker, { MaterialVisual } from './IconColorPicker';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  amber: '#f59e0b', amberBg: '#fef3c7', error: '#a83836',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...sx }}>{name}</span>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>{children}</Typography>
);
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem', bgcolor: T.surface } };

export default function SetPopup({ open, set, allTypes = [], onClose, onChanged }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isNew = !set;
  const f = set?.fields || {};

  const [form, setForm] = useState({ name: '', description: '', icon: null, color: null });
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  // Gearchiveerde materialen niet toevoegbaar aan sets
  const typeOptions = useMemo(() => allTypes.filter(t => !t.fields?.Gearchiveerd).map(t => ({ id: t.id, label: t.fields?.MateriaalType || 'Materiaal' })), [allTypes]);

  useEffect(() => {
    if (open) {
      setForm({ name: f.ProjectType || '', description: f.Omschrijving || '', icon: f.Icoon || null, color: f.Kleur || null });
      const ids = f.MaterialenType || [];
      setSelectedTypes(typeOptions.filter(o => ids.includes(o.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, set, allTypes]);

  const archived = !!f.Gearchiveerd;

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) { toast.error('Naam is verplicht'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${BACKEND_URL}/Api/materiaalsets`, {
          name, description: form.description || null, typeIds: selectedTypes.map(t => t.id),
          icon: form.icon, color: form.color,
        });
      } else {
        await axios.patch(`${BACKEND_URL}/Api/materiaalsets/${set.id}`, { fields: {
          ProjectType: name, Omschrijving: form.description,
          MaterialenType: selectedTypes.map(t => t.id), Icoon: form.icon, Kleur: form.color,
        } });
      }
      toast.success(isNew ? 'Set aangemaakt' : 'Set opgeslagen');
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleArchiveToggle = async () => {
    if (isNew) return;
    setSaving(true);
    try {
      await axios.patch(`${BACKEND_URL}/Api/materiaalsets/${set.id}`, { fields: { Gearchiveerd: !archived } });
      toast.success(!archived ? 'Set gearchiveerd' : 'Set teruggezet');
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error('Mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '1rem', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${T.surfaceHigh}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '0.6rem', flexShrink: 0, overflow: 'hidden' }}>
          <MaterialVisual icon={form.icon || 'backpack'} color={form.color || T.tertiary} fallbackIcon="backpack" iconSize={21} />
        </Box>
        <Typography sx={{ flex: 1, fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>
          {isNew ? 'Nieuwe set' : (f.ProjectType || 'Set bewerken')}
        </Typography>
        <IconButton onClick={onClose} size="small"><Icon name="close" size={20} sx={{ color: T.outline }} /></IconButton>
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {archived && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '0.6rem', bgcolor: T.surfaceLow }}>
            <Icon name="archive" size={16} sx={{ color: T.outline }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>Deze set is gearchiveerd en verschijnt niet bij reserveringen.</Typography>
          </Box>
        )}
        <Box>
          <FieldLabel>Naam</FieldLabel>
          <TextField size="small" fullWidth value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} sx={inputSx} />
        </Box>
        <Box>
          <FieldLabel>Omschrijving</FieldLabel>
          <TextField size="small" fullWidth multiline minRows={2} value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} sx={inputSx} />
        </Box>
        <IconColorPicker icon={form.icon} color={form.color} onChange={({ icon, color }) => setForm(p => ({ ...p, icon, color }))} label="Icoon & kleur" />
        <Box>
          <FieldLabel>Materialen in deze set</FieldLabel>
          <Autocomplete
            multiple size="small" options={typeOptions} value={selectedTypes}
            onChange={(_, v) => setSelectedTypes(v)} getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderTags={(value, getTagProps) => value.map((opt, index) => (
              <Chip {...getTagProps({ index })} key={opt.id} label={opt.label} size="small"
                sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, bgcolor: T.surfaceLow, color: T.secondary }} />
            ))}
            renderInput={(params) => <TextField {...params} placeholder="Materiaal toevoegen…" sx={inputSx} />}
          />
        </Box>
      </DialogContent>

      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${T.surfaceHigh}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isNew && (
          <Tooltip title={archived ? 'Terugzetten' : 'Archiveren'} arrow>
            <Box onClick={() => !saving && handleArchiveToggle()} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: archived ? T.primary : T.secondary, border: `1px solid ${T.surfaceHigh}`, '&:hover': { bgcolor: T.surfaceLow } }}>
              <Icon name={archived ? 'unarchive' : 'archive'} size={16} /> {archived ? 'Terugzetten' : 'Archiveren'}
            </Box>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Box onClick={onClose} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
        <Box onClick={() => !saving && handleSave()} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: saving ? 'default' : 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', opacity: saving ? 0.7 : 1 }}>
          {saving && <CircularProgress size={14} sx={{ color: '#fff' }} />} {isNew ? 'Aanmaken' : 'Opslaan'}
        </Box>
      </Box>
    </Dialog>
  );
}
